'use client';

import { useState } from 'react';
import AccordionCard from './AccordionCard';
import CartSummary from './CartSummary';
import BusinessInfo from './BusinessInfo';
import AccountInfo from './AccountInfo';
import PaymentInfo from './PaymentInfo';
import { GoArrowLeft } from 'react-icons/go';
import Button from 'components/generic-components/thera-start-components/Button';
import * as Yup from 'yup';
import { getPrice, getName, getSelectedState } from 'lib/theraStartConstants';

// Special boolean for test mode. Set to true to force total to $5 for prod testing.
const isTestMode = false;

// Validation schemas for each step
const accountSchema = Yup.object({
  password: Yup.string().min(10, 'Please fulfill all the requirements').required('Please fulfill all the requirements'),
});

const paymentSchema = Yup.object({
  card: Yup.object({
    number: Yup.string()
      .matches(/^\d{16}$/, 'Invalid card number')
      .required('Card number is required'),
    expiry: Yup.string()
      .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Invalid expiry date')
      .required('Expiry date is required'),
    cvv: Yup.string()
      .matches(/^\d{3,4}$/, 'Invalid CVV')
      .required('CVV is required'),
    zip: Yup.string()
      .matches(/^\d{5}$/, 'Invalid ZIP code')
      .required('ZIP code is required'),
  }),
});
export default function CheckoutAccordionStep({ formik, onBack }) {
  const [open, setOpen] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [errors, setErrors] = useState({} as any);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [card, setCard] = useState({
    number: '',
    expiry: '',
    cvv: '',
    zip: '',
  });

  let totalAmount = 0;

  const {
    package: selectedPackage,
    worryFreeCompliance,
    einDocuments,
    operatingAgreement,
    fasterFiling,
  } = formik.values;

  const state = formik?.values?.state;

  const selectedState = getSelectedState(state);

  // Build breakdown array
  const items = [];
  if (selectedPackage === 'pro') items.push({ name: 'Pro', value: 250 });
  if (selectedPackage !== 'pro') items.push({ name: 'Starter', value: 0 });
  if (selectedState) {
    items.push({ name: `${selectedState.State} state filing fee`, value: selectedState.TotalStandardStateFee });
    totalAmount += selectedState.TotalStandardStateFee;
  }
  if (worryFreeCompliance) items.push({ name: 'Worry Free Compliance', value: 250 });
  if (operatingAgreement) items.push({ name: 'Trademark Service', value: 499 });
  if (fasterFiling && selectedState) {
    items.push({ name: `${selectedState?.State} DBA Services`, value: selectedState.TotalDBAFees || 0 });
    totalAmount += selectedState?.TotalDBAFees || 0;
  }
  if (einDocuments?.length) {
    einDocuments.forEach((doc) => {
      const price = getPrice(doc);
      if (price) items.push({ name: getName(doc), value: price });
    });
  }

  if (selectedPackage === 'pro') totalAmount += 250;
  if (worryFreeCompliance) totalAmount += 250;
  if (operatingAgreement) totalAmount += 499;
  if (einDocuments?.length) {
    einDocuments.map((doc) => {
      const price = getPrice(doc);
      if (price) totalAmount += price;
    });
  }

  // If test mode is enabled and state is Oklahoma, override total and items for $5 test checkout
  if (isTestMode && selectedState?.State === 'Oklahoma') {
    totalAmount = 5;
    items.length = 0; // Clear existing items
    items.push({ name: 'Test Mode: Oklahoma Special $5 Total', value: 5 });
  }

  const validateStep = async (step) => {
    try {
      if (step === 2) {
        await accountSchema.validate({ password }, { abortEarly: false });
      } else if (step === 3) {
        await paymentSchema.validate({ card }, { abortEarly: false });
      }
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = {} as any;
        err.inner.forEach((error) => {
          // For password step, only show one generic error
          if (step === 2) {
            newErrors.password = 'Please fulfill all the requirements';
          } else {
            // For payment step, show specific errors
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleContinue = async (step) => {
    const isValid = await validateStep(step);
    if (isValid) {
      setErrors({});
      setCompletedSteps((prev) => [...prev, step]);
      setOpen(step + 1);
    }
  };

  const isStepCompleted = (step) => completedSteps.includes(step);

  const getLastCompletedStep = () => {
    return Math.max(...completedSteps, -1);
  };

  const canOpenStep = (step) => {
    const lastCompleted = getLastCompletedStep();
    return isStepCompleted(step) || step === lastCompleted + 1;
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-[1.5rem] flex items-center gap-[0.5rem] text-[1rem] font-semibold text-[#191919] transition-colors hover:text-therablue"
      >
        <GoArrowLeft size={18} className="text-white" />
        <span className="font-bold text-white">Back</span>
      </button>

      <h2 className="mb-[2rem] text-[2rem] font-bold leading-[2.5rem] text-white">Checkout</h2>

      <div className="flex w-full flex-col gap-[1.5rem]">
        {/* Step 1: Cart */}
        <AccordionCard
          open={open === 0}
          onClick={() => canOpenStep(0) && setOpen(open === 0 ? -1 : 0)}
          step={1}
          completed={isStepCompleted(0)}
          label="Your Cart"
        >
          <CartSummary values={formik.values} />
          {!isStepCompleted(0) && (
            <Button
              onClick={() => handleContinue(0)}
              className="mt-4 w-full rounded bg-dark px-4 py-[0.5rem] font-[300] text-white transition-colors hover:bg-blue-700"
            >
              Continue
            </Button>
          )}
        </AccordionCard>

        {/* Step 2: Business Info */}
        <AccordionCard
          open={open === 1}
          onClick={() => canOpenStep(1) && setOpen(open === 1 ? -1 : 1)}
          step={2}
          completed={isStepCompleted(1)}
          label="Business Info"
        >
          <BusinessInfo formik={formik} />
          {!isStepCompleted(1) && (
            <Button
              onClick={() => handleContinue(1)}
              className="mt-4 w-full rounded bg-dark px-4 py-[0.5rem] font-[300] text-white transition-colors hover:bg-blue-700"
            >
              Continue
            </Button>
          )}
        </AccordionCard>

        {/* Step 3: Account Info */}
        <AccordionCard
          open={open === 2}
          onClick={() => canOpenStep(2) && setOpen(open === 2 ? -1 : 2)}
          step={3}
          completed={isStepCompleted(2)}
          label="Account Info"
        >
          <AccountInfo
            formik={formik}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
          {errors.password && <div className="mt-2 text-sm text-red-500">{errors.password}</div>}
          {!isStepCompleted(2) && (
            <Button
              onClick={() => handleContinue(2)}
              className="mt-4 w-full rounded bg-dark px-4 py-[0.5rem] font-[300] text-white transition-colors hover:bg-blue-700"
            >
              Continue
            </Button>
          )}
        </AccordionCard>

        {/* Step 4: Payment */}
        <AccordionCard
          open={open === 3}
          onClick={() => canOpenStep(3) && setOpen(open === 3 ? -1 : 3)}
          step={4}
          completed={isStepCompleted(3)}
          label="Payment"
        >
          <PaymentInfo formik={formik} totalAmount={totalAmount} items={items} password={password} />
        </AccordionCard>
      </div>
    </>
  );
}
